import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as SubmitIcon,
  RateReview as ReviewIcon,
  ThumbDown as RejectIcon,
  CheckCircle as AcceptIcon,
  Cancel as CancelIcon,
  Paid as PayIcon,
  Edit as AmendIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, CurrencyPicker, FiscalPeriodPicker } from '../LedgerPickers';
import {
  TaxReturnStatusChip,
  TaxPaymentStatusChip,
  StatStrip,
  taxReturnIdOf,
  taxLineIdOf,
  fmtDate,
  getErrorMessage,
  unwrap,
  unwrapList,
  RETURN_TYPES,
  RETURN_STATUSES,
  PAYMENT_STATUSES,
  LINE_TYPES,
  SUBMISSION_METHODS,
} from './TaxShared';

const emptyForm = () => ({
  return_type: 'VAT',
  period_start: '',
  period_end: '',
  filing_due_date: '',
  currency_id: '',
  total_tax_due: '',
  submission_method: 'PORTAL',
  period_id: '',
  authority_ref: '',
  notes: '',
});

const emptyLine = () => ({ line_number: 1, line_type: 'OUTPUT_VAT', description: '', amount: '', tax_rate: '', source_account_id: '' });

// Tax returns: DRAFT -> IN_REVIEW -> APPROVED -> SUBMITTED -> ACCEPTED, then
// post; reject/cancel/amend loop back with reasons (min 10 chars). Lines
// carry the return's computed amounts and can be suggested from the GL.
// Verified against accounting-apis tax-returns controller + DTOs.
const TaxReturnsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.TAX, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.TAX, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.TAX, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.TAX, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState(null);

  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    ledgerAPI
      .getTaxReturnStats()
      .then((res) => setStats(unwrap(res)))
      .catch(() => setStats(null));
  }, [refreshKey]);

  const submitCreate = async () => {
    if (!values.period_start || !values.period_end || !values.filing_due_date) {
      showSnackbar('Period start, period end and filing due date are required', 'error');
      return;
    }
    if (!values.currency_id) {
      showSnackbar('Currency is required', 'error');
      return;
    }
    if (values.total_tax_due === '' || !(Number(values.total_tax_due) >= 0)) {
      showSnackbar('Total tax due must be zero or more', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.createTaxReturn({
        return_type: values.return_type,
        period_start: values.period_start,
        period_end: values.period_end,
        filing_due_date: values.filing_due_date,
        currency_id: Number(values.currency_id),
        total_tax_due: Number(values.total_tax_due),
        ...(values.submission_method ? { submission_method: values.submission_method } : {}),
        ...(values.period_id ? { period_id: Number(values.period_id) } : {}),
        ...(values.authority_ref?.trim() ? { authority_ref: values.authority_ref.trim() } : {}),
        ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
      });
      showSnackbar('Tax return created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const [retRes, linesRes] = await Promise.all([
        ledgerAPI.getTaxReturn(taxReturnIdOf(row)),
        ledgerAPI.getTaxReturnLines(taxReturnIdOf(row), { limit: 100 }).catch(() => null),
      ]);
      setDetail(unwrap(retRes));
      setLines(linesRes ? unwrapList(linesRes) : Array.isArray(unwrap(retRes)?.lines) ? unwrap(retRes).lines : []);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch tax return'), 'error');
    }
  };

  const refreshDetail = async (id) => {
    try {
      const [retRes, linesRes] = await Promise.all([
        ledgerAPI.getTaxReturn(id),
        ledgerAPI.getTaxReturnLines(id, { limit: 100 }).catch(() => null),
      ]);
      setDetail(unwrap(retRes));
      setLines(linesRes ? unwrapList(linesRes) : []);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to refresh'), 'error');
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this tax return?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Tax return ${pastTense} successfully`, 'success');
          if (detail) refreshDetail(taxReturnIdOf(detail));
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await ledgerAPI.updateTaxReturn(taxReturnIdOf(editing.src), editing.values);
      showSnackbar('Tax return updated', 'success');
      setEditing(null);
      if (detail) refreshDetail(taxReturnIdOf(detail));
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const [lineDlg, setLineDlg] = useState({ open: false, editing: null, values: emptyLine() });
  const [reasonDlg, setReasonDlg] = useState({ open: false, kind: null, item: null, reason: '', extra: {} });
  const [payDlg, setPayDlg] = useState({ open: false, item: null, payment_date: new Date().toISOString().slice(0, 10), amount_paid: '', reconciliation_account_id: '', payment_reference: '', notes: '' });
  const [submitDlg, setSubmitDlg] = useState({ open: false, item: null, filed_date: new Date().toISOString().slice(0, 10), submission_method: 'PORTAL', authority_ref: '', acknowledgment_ref: '', notes: '' });

  const submitLine = async () => {
    const v = lineDlg.values;
    if (!v.description?.trim() || v.amount === '' || !v.line_type) {
      showSnackbar('Description, amount and line type are required', 'error');
      return;
    }
    const payload = {
      line_number: Number(v.line_number) || 1,
      line_type: v.line_type,
      description: v.description.trim(),
      amount: Number(v.amount),
      ...(v.tax_rate !== '' ? { tax_rate: Number(v.tax_rate) } : {}),
      ...(v.source_account_id ? { source_account_id: Number(v.source_account_id) } : {}),
    };
    try {
      const id = taxReturnIdOf(detail);
      if (lineDlg.editing) {
        await ledgerAPI.updateTaxReturnLine(id, taxLineIdOf(lineDlg.editing), payload);
        showSnackbar('Line updated', 'success');
      } else {
        await ledgerAPI.createTaxReturnLine(id, payload);
        showSnackbar('Line added', 'success');
      }
      setLineDlg({ open: false, editing: null, values: emptyLine() });
      refreshDetail(id);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const suggestFill = async () => {
    if (!detail) return;
    const id = taxReturnIdOf(detail);
    try {
      const res = await ledgerAPI.suggestTaxLines({
        return_type: detail.return_type,
        period_start: detail.period_start,
        period_end: detail.period_end,
      });
      const suggested = unwrap(res);
      const list = Array.isArray(suggested) ? suggested : suggested.lines ?? [];
      if (!list.length) {
        showSnackbar('No suggested lines for this period', 'error');
        return;
      }
      await ledgerAPI.bulkCreateTaxReturnLines(id, {
        lines: list.map((l, i) => ({
          line_number: l.line_number ?? i + 1,
          line_type: l.line_type,
          description: l.description,
          amount: Number(l.amount ?? 0),
          ...(l.tax_rate != null ? { tax_rate: Number(l.tax_rate) } : {}),
          ...(l.source_account_id ? { source_account_id: Number(l.source_account_id) } : {}),
        })),
      });
      showSnackbar(`${list.length} suggested line(s) added`, 'success');
      refreshDetail(id);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitReason = async () => {
    const { kind, item, reason, extra } = reasonDlg;
    const id = taxReturnIdOf(item);
    try {
      if (kind === 'reject') await ledgerAPI.rejectTaxReturn(id, { rejection_reason: reason.trim(), ...extra });
      else if (kind === 'cancel') await ledgerAPI.cancelTaxReturn(id, { cancellation_reason: reason.trim(), ...extra });
      else if (kind === 'amend') await ledgerAPI.amendTaxReturn(id, { amendment_reason: reason.trim(), ...extra });
      showSnackbar(`Tax return ${kind === 'amend' ? 'amended' : kind === 'reject' ? 'rejected' : 'cancelled'} successfully`, 'success');
      setReasonDlg({ open: false, kind: null, item: null, reason: '', extra: {} });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitPayment = async () => {
    if (!(Number(payDlg.amount_paid) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!payDlg.reconciliation_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    try {
      await ledgerAPI.recordTaxPayment(taxReturnIdOf(payDlg.item), {
        payment_date: payDlg.payment_date,
        amount_paid: Number(payDlg.amount_paid),
        reconciliation_account_id: Number(payDlg.reconciliation_account_id),
        ...(payDlg.payment_reference?.trim() ? { payment_reference: payDlg.payment_reference.trim() } : {}),
        ...(payDlg.notes?.trim() ? { notes: payDlg.notes.trim() } : {}),
      });
      showSnackbar('Payment recorded', 'success');
      setPayDlg({ open: false, item: null, payment_date: '', amount_paid: '', reconciliation_account_id: '', payment_reference: '', notes: '' });
      if (detail) refreshDetail(taxReturnIdOf(detail));
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitFiling = async () => {
    if (!submitDlg.filed_date) {
      showSnackbar('Filed date is required', 'error');
      return;
    }
    try {
      await ledgerAPI.submitTaxReturn(taxReturnIdOf(submitDlg.item), {
        filed_date: submitDlg.filed_date,
        ...(submitDlg.submission_method ? { submission_method: submitDlg.submission_method } : {}),
        ...(submitDlg.authority_ref?.trim() ? { authority_ref: submitDlg.authority_ref.trim() } : {}),
        ...(submitDlg.acknowledgment_ref?.trim() ? { acknowledgment_ref: submitDlg.acknowledgment_ref.trim() } : {}),
        ...(submitDlg.notes?.trim() ? { notes: submitDlg.notes.trim() } : {}),
      });
      showSnackbar('Tax return submitted', 'success');
      setSubmitDlg({ open: false, item: null, filed_date: '', submission_method: 'PORTAL', authority_ref: '', acknowledgment_ref: '', notes: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailStatus = String(detail?.status || 'DRAFT').toUpperCase();
  const statTiles = stats
    ? Object.entries(stats.totals ?? stats)
        .filter(([, v]) => typeof v === 'number')
        .slice(0, 5)
        .map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v }))
    : [];

  return (
    <>
      {statTiles.length > 0 && <StatStrip tiles={statTiles} />}
      <LedgerListPage
        resource={RESOURCES.LEDGER.TAX}
        title="Tax Returns"
        subtitle="VAT, WHT, PAYE and other filings - from draft to acceptance and payment"
        addLabel="New Tax Return"
        idKey="return_id"
        searchPlaceholder="Search authority ref..."
        emptyMessage="No tax returns found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getTaxReturns(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: RETURN_STATUSES.map((s) => ({ label: s.replace(/_/g, ' '), value: s })) },
          { key: 'return_type', label: 'Type', options: RETURN_TYPES },
          { key: 'payment_status', label: 'Payment', options: PAYMENT_STATUSES.map((s) => ({ label: s.replace(/_/g, ' '), value: s })) },
          { key: 'overdue_only', label: 'Overdue', options: [{ label: 'Overdue only', value: true }] },
          { type: 'daterange', keyFrom: 'period_start_from', keyTo: 'period_start_to', label: 'Period start' },
          { type: 'daterange', keyFrom: 'filing_due_date_from', keyTo: 'filing_due_date_to', label: 'Due date' },
        ]}
        columns={[
          { label: 'Type', render: (r) => <strong>{String(r.return_type || '').replace(/_/g, ' ')}</strong> },
          { label: 'Period', render: (r) => `${fmtDate(r.period_start)} - ${fmtDate(r.period_end)}`, hideOnMobile: true },
          { label: 'Due', render: (r) => fmtDate(r.filing_due_date) },
          { label: 'Tax Due', render: (r) => formatAccountingAmount(r.total_tax_due ?? r.total_amount), align: 'right' },
          { label: 'Status', render: (r) => <TaxReturnStatusChip status={r.status} /> },
          { label: 'Payment', render: (r) => <TaxPaymentStatusChip status={r.payment_status} />, hideOnMobile: true },
        ]}
        rowActions={(r) => {
          const id = taxReturnIdOf(r);
          const status = String(r.status || 'DRAFT').toUpperCase();
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(r) }];
          if (status === 'DRAFT' && canUpdate) {
            actions.push({ icon: EditIcon, title: 'Edit', color: 'primary', onClick: () => setEditing({ src: r, values: { period_start: r.period_start?.slice(0, 10) || '', period_end: r.period_end?.slice(0, 10) || '', filing_due_date: r.filing_due_date?.slice(0, 10) || '', total_tax_due: r.total_tax_due ?? '', authority_ref: r.authority_ref || '', notes: r.notes || '' } }) });
          }
          if (status === 'DRAFT' && canCreate) {
            actions.push({ icon: ReviewIcon, title: 'Submit for review', color: 'primary', onClick: () => runWorkflow('Submit for review', 'submitted for review', () => ledgerAPI.submitTaxReturnForReview(id)) });
          }
          if (status === 'IN_REVIEW' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve return', 'approved', () => ledgerAPI.approveTaxReturn(id)) });
            actions.push({ icon: RejectIcon, title: 'Reject', color: 'error', onClick: () => setReasonDlg({ open: true, kind: 'reject', item: r, reason: '', extra: {} }) });
          }
          if (status === 'APPROVED' && canApprove) {
            actions.push({ icon: SubmitIcon, title: 'File with authority', color: 'primary', onClick: () => setSubmitDlg({ open: true, item: r, filed_date: new Date().toISOString().slice(0, 10), submission_method: 'PORTAL', authority_ref: '', acknowledgment_ref: '', notes: '' }) });
            actions.push({ icon: RejectIcon, title: 'Send back (unapprove)', color: 'default', onClick: () => runWorkflow('Unapprove return', 'sent back', () => ledgerAPI.unapproveTaxReturn(id)) });
          }
          if (status === 'SUBMITTED' && canApprove) {
            actions.push({ icon: AcceptIcon, title: 'Accept', color: 'primary', onClick: () => runWorkflow('Accept return', 'accepted', () => ledgerAPI.acceptTaxReturn(id)) });
            actions.push({ icon: AmendIcon, title: 'Amend', color: 'default', onClick: () => setReasonDlg({ open: true, kind: 'amend', item: r, reason: '', extra: {} }) });
          }
          if (status === 'ACCEPTED' && canApprove) {
            actions.push({ icon: SubmitIcon, title: 'Post to GL', color: 'primary', onClick: () => runWorkflow('Post return', 'posted', () => ledgerAPI.postTaxReturn(id)) });
          }
          if (!['CANCELLED'].includes(status) && canApprove) {
            actions.push({ icon: PayIcon, title: 'Record payment', color: 'primary', onClick: () => setPayDlg({ open: true, item: r, payment_date: new Date().toISOString().slice(0, 10), amount_paid: '', reconciliation_account_id: '', payment_reference: '', notes: '' }) });
          }
          if (!['CANCELLED', 'ACCEPTED'].includes(status) && canApprove) {
            actions.push({ icon: CancelIcon, title: 'Cancel', color: 'error', onClick: () => setReasonDlg({ open: true, kind: 'cancel', item: r, reason: '', extra: {} }) });
          }
          if (status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete tax return',
                message: 'Delete this DRAFT return? This cannot be undone.',
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteTaxReturn(id);
                    showSnackbar('Tax return deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Tax Return</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" select label="Return Type" value={values.return_type} onChange={(e) => setValues((p) => ({ ...p, return_type: e.target.value }))} required>
                {RETURN_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="date" label="Period Start" value={values.period_start} onChange={(e) => setValues((p) => ({ ...p, period_start: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="date" label="Period End" value={values.period_end} onChange={(e) => setValues((p) => ({ ...p, period_end: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="date" label="Filing Due Date" value={values.filing_due_date} onChange={(e) => setValues((p) => ({ ...p, filing_due_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <CurrencyPicker value={values.currency_id} onChange={(id) => setValues((p) => ({ ...p, currency_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="number" label="Total Tax Due" value={values.total_tax_due} onChange={(e) => setValues((p) => ({ ...p, total_tax_due: e.target.value }))} inputProps={{ min: 0, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" select label="Submission Method" value={values.submission_method} onChange={(e) => setValues((p) => ({ ...p, submission_method: e.target.value }))}>
                {SUBMISSION_METHODS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FiscalPeriodPicker value={values.period_id} onChange={(id) => setValues((p) => ({ ...p, period_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" label="Authority Ref" value={values.authority_ref} onChange={(e) => setValues((p) => ({ ...p, authority_ref: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Notes" value={values.notes} onChange={(e) => setValues((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail ? `${String(detail.return_type || '').replace(/_/g, ' ')} - ${fmtDate(detail.period_start)} to ${fmtDate(detail.period_end)}` : ''}
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <TaxReturnStatusChip status={detail.status} />
                <TaxPaymentStatusChip status={detail.payment_status} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  Due {fmtDate(detail.filing_due_date)} - Total {formatAccountingAmount(detail.total_tax_due)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Return lines</Typography>
                <Box display="flex" gap={1}>
                  {detailStatus === 'DRAFT' && canCreate && (
                    <Button size="small" variant="outlined" onClick={suggestFill}>Suggest from GL</Button>
                  )}
                  {detailStatus === 'DRAFT' && canCreate && (
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLineDlg({ open: true, editing: null, values: { ...emptyLine(), line_number: lines.length + 1 } })}>Add Line</Button>
                  )}
                  {detailStatus === 'DRAFT' && canApprove && (
                    <Button size="small" variant="outlined" onClick={() => runWorkflow('Refresh totals', 'refreshed', () => ledgerAPI.refreshTaxTotals(taxReturnIdOf(detail)))}>Refresh Totals</Button>
                  )}
                </Box>
              </Box>
              <DataTable
                columns={['#', 'Type', 'Description', 'Amount', 'Rate %', 'Actions']}
                data={lines}
                loading={false}
                emptyMessage="No lines yet."
                renderRow={(l) => (
                  <TableRow key={taxLineIdOf(l)}>
                    <TableCell>{l.line_number}</TableCell>
                    <TableCell>{String(l.line_type || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell>{l.description}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.amount)}</TableCell>
                    <TableCell align="right">{l.tax_rate ?? '-'}</TableCell>
                    <TableCell>
                      {detailStatus === 'DRAFT' && canUpdate && (
                        <Box display="flex" gap={0.5}>
                          <IconButton size="small" color="primary" title="Edit line" onClick={() => setLineDlg({ open: true, editing: l, values: { line_number: l.line_number ?? 1, line_type: l.line_type, description: l.description || '', amount: l.amount ?? '', tax_rate: l.tax_rate ?? '', source_account_id: l.source_account_id ?? '' } })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Delete line" onClick={() => confirm({
                            title: 'Delete line',
                            message: 'Delete this return line?',
                            onConfirm: async () => {
                              try {
                                await ledgerAPI.deleteTaxReturnLine(taxReturnIdOf(detail), taxLineIdOf(l));
                                showSnackbar('Line deleted', 'success');
                                refreshDetail(taxReturnIdOf(detail));
                              } catch (error) {
                                showSnackbar(getErrorMessage(error), 'error');
                              }
                            },
                          })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              />
              {(detail.rejection_reason || detail.cancellation_reason || detail.notes) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {[detail.rejection_reason && `Rejected: ${detail.rejection_reason}`, detail.cancellation_reason && `Cancelled: ${detail.cancellation_reason}`, detail.notes].filter(Boolean).join(' | ')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineDlg.open} onClose={() => setLineDlg({ open: false, editing: null, values: emptyLine() })} maxWidth="sm" fullWidth>
        <DialogTitle>{lineDlg.editing ? 'Edit Return Line' : 'Add Return Line'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={4}>
              <TextField fullWidth size="small" type="number" label="Line #" value={lineDlg.values.line_number} onChange={(e) => setLineDlg((p) => ({ ...p, values: { ...p.values, line_number: e.target.value } }))} inputProps={{ min: 1, step: 1 }} />
            </Grid>
            <Grid size={8}>
              <TextField fullWidth size="small" select label="Line Type" value={lineDlg.values.line_type} onChange={(e) => setLineDlg((p) => ({ ...p, values: { ...p.values, line_type: e.target.value } }))} required>
                {LINE_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Description" value={lineDlg.values.description} onChange={(e) => setLineDlg((p) => ({ ...p, values: { ...p.values, description: e.target.value } }))} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" type="number" label="Amount" value={lineDlg.values.amount} onChange={(e) => setLineDlg((p) => ({ ...p, values: { ...p.values, amount: e.target.value } }))} inputProps={{ step: 'any' }} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" type="number" label="Tax Rate %" value={lineDlg.values.tax_rate} onChange={(e) => setLineDlg((p) => ({ ...p, values: { ...p.values, tax_rate: e.target.value } }))} inputProps={{ min: 0, max: 100, step: 'any' }} />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Source Account (optional)" value={lineDlg.values.source_account_id} onChange={(id) => setLineDlg((p) => ({ ...p, values: { ...p.values, source_account_id: id } }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineDlg({ open: false, editing: null, values: emptyLine() })}>Cancel</Button>
          <Button variant="contained" onClick={submitLine}>Save Line</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={reasonDlg.open}
        onClose={() => setReasonDlg({ open: false, kind: null, item: null, reason: '', extra: {} })}
        title={reasonDlg.kind === 'reject' ? 'Reject return (min 10 chars)' : reasonDlg.kind === 'amend' ? 'Amend return (min 10 chars)' : 'Cancel return (min 10 chars)'}
        onSubmit={submitReason}
        values={reasonDlg}
        onChange={(key, value) => setReasonDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'reason', label: 'Reason', type: 'textarea', required: true, md: 12 }]}
      />

      <LedgerFormDialog
        open={payDlg.open}
        onClose={() => setPayDlg({ open: false, item: null, payment_date: '', amount_paid: '', reconciliation_account_id: '', payment_reference: '', notes: '' })}
        title="Record tax payment"
        onSubmit={submitPayment}
        values={payDlg}
        onChange={(key, value) => setPayDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'payment_date', label: 'Payment Date', type: 'date', required: true, md: 6 },
          { key: 'amount_paid', label: 'Amount Paid', type: 'number', required: true, min: 0.01, md: 6 },
          { key: 'reconciliation_account_id', label: 'Paid From (bank/cash account)', type: 'picker', picker: AccountPicker, required: true, md: 12 },
          { key: 'payment_reference', label: 'Payment Reference', md: 6 },
          { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
        ]}
      />

      <LedgerFormDialog
        open={submitDlg.open}
        onClose={() => setSubmitDlg({ open: false, item: null, filed_date: '', submission_method: 'PORTAL', authority_ref: '', acknowledgment_ref: '', notes: '' })}
        title="File return with authority"
        onSubmit={submitFiling}
        values={submitDlg}
        onChange={(key, value) => setSubmitDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'filed_date', label: 'Filed Date', type: 'date', required: true, md: 6 },
          { key: 'submission_method', label: 'Method', type: 'select', options: SUBMISSION_METHODS, md: 6 },
          { key: 'authority_ref', label: 'Authority Ref', md: 6 },
          { key: 'acknowledgment_ref', label: 'Acknowledgment Ref', md: 6 },
          { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
        ]}
      />

      <LedgerFormDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit tax return (DRAFT only)"
        onSubmit={submitEdit}
        values={editing?.values || {}}
        onChange={(key, value) => setEditing((p) => (p ? { ...p, values: { ...p.values, [key]: value } } : p))}
        maxWidth="sm"
        fields={[
          { key: 'period_start', label: 'Period Start', type: 'date', md: 6 },
          { key: 'period_end', label: 'Period End', type: 'date', md: 6 },
          { key: 'filing_due_date', label: 'Filing Due Date', type: 'date', md: 6 },
          { key: 'total_tax_due', label: 'Total Tax Due', type: 'number', min: 0, md: 6 },
          { key: 'authority_ref', label: 'Authority Ref', md: 6 },
          { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
        ]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default TaxReturnsTab;
